import type { TimelineItem } from '@/types/timeline';

export type PreviewProfile = 'normal' | 'dense';
export type RenderIntent = 'full' | 'static' | 'poster';

interface AdaptivePreviewProfileArgs {
  items: TimelineItem[];
  compositionItems?: TimelineItem[];
}

interface ResolvePreviewRenderIntentArgs {
  previewProfile?: PreviewProfile;
  item: Pick<TimelineItem, 'id' | 'type' | 'from' | 'durationInFrames'>;
  currentFrame: number;
  fps: number;
  isPlaying: boolean;
  isSelected: boolean;
  isPreviewTarget?: boolean;
}

interface AdaptivePreloadWindowArgs {
  fps: number;
  isPlaying: boolean;
}

export function getAdaptivePreviewProfile({
  items,
  compositionItems = [],
}: AdaptivePreviewProfileArgs): PreviewProfile {
  const allItems = compositionItems.length > 0 ? items.concat(compositionItems) : items;
  const visualItemCount = allItems.filter((item) =>
    item.type === 'video' || item.type === 'image' || item.type === 'composition'
  ).length;
  const videoItemCount = allItems.filter((item) => item.type === 'video').length;

  if (items.length >= 80 || visualItemCount >= 40 || videoItemCount >= 3) {
    return 'dense';
  }

  return 'normal';
}

export function getAdaptivePreloadWindowFrames({
  fps,
  isPlaying,
}: AdaptivePreloadWindowArgs): { beforeFrames: number; afterFrames: number } {
  const beforeFrames = Math.round(fps * 0.75);
  const afterFrames = Math.round(fps * (isPlaying ? 5 : 1.5));
  return { beforeFrames, afterFrames };
}

export function getAdaptivePreloadBucket(frame: number, fps: number): number {
  const bucketSize = Math.max(1, Math.round(fps * 0.5));
  return Math.floor(frame / bucketSize);
}

export function resolvePreviewRenderIntent({
  previewProfile = 'normal',
  item,
  currentFrame,
  fps,
  isPlaying,
  isSelected,
  isPreviewTarget = false,
}: ResolvePreviewRenderIntentArgs): RenderIntent {
  if (previewProfile !== 'dense' || isSelected || isPreviewTarget) {
    return 'full';
  }

  const beforeFrames = Math.round(fps * 0.35);
  const afterFrames = Math.round(fps * (isPlaying ? 1 : 0.35));
  const windowStart = item.from - beforeFrames;
  const windowEnd = item.from + item.durationInFrames + afterFrames;
  const isNearPlayhead = currentFrame >= windowStart && currentFrame < windowEnd;

  if (isNearPlayhead) {
    return 'full';
  }

  return item.type === 'video' ? 'poster' : 'static';
}
